<script setup lang="ts">
import { formatLongFr, type Me } from '@cockpit/shared';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ROLE_LABELS } from '../navigation';
import { useFeedbackPanelStore } from '../stores/feedbackPanel';
import AppTabs from './AppTabs.vue';
import ClientSearch from './ClientSearch.vue';
import NotificationBell from './NotificationBell.vue';
import UserAvatar from './UserAvatar.vue';

const props = defineProps<{ me: Me; today: string }>();
const emit = defineEmits<{ logout: [] }>();

const router = useRouter();
const menuOpen = ref(false);
/** Lot AQ : « Signaler un problème » ouvre le panneau « Faire un retour » sans quitter la page. */
const feedbackPanel = useFeedbackPanelStore();

async function openNotifications(): Promise<void> {
  menuOpen.value = false;
  await router.push('/notifications');
}
const root = ref<HTMLElement | null>(null);
const dateLabel = computed(() => formatLongFr(props.today));

function onDocumentClick(event: MouseEvent): void {
  if (menuOpen.value && root.value && !root.value.contains(event.target as Node)) menuOpen.value = false;
}
function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') menuOpen.value = false;
}
onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <header class="top">
    <div class="wrap">
      <div class="top-row">
        <div class="brand">
          <h1>Cockpit Evolusite</h1>
          <span class="date">{{ dateLabel }}</span>
        </div>
        <div ref="root" class="who">
          <!-- Lot AV : recherche de client (Ctrl / ⌘ + K), vers la page du client. -->
          <ClientSearch />
          <RouterLink class="btn sm" to="/ecran" data-testid="tv-button">Mode écran</RouterLink>
          <button class="btn sm" type="button" data-testid="report-button" @click="feedbackPanel.show()">
            Signaler un problème
          </button>
          <!-- Lot AJ : cloche des notifications (non lues, dernières, en direct). -->
          <NotificationBell @open="menuOpen = false" />
          <button
            class="userbtn"
            type="button"
            aria-haspopup="true"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            data-testid="user-button"
            @click="menuOpen = !menuOpen"
          >
            <UserAvatar :initials="me.initials" :color="me.color" />
            <span
              >{{ me.name }} <small>· {{ ROLE_LABELS[me.role] }}</small></span
            >
          </button>
          <div v-if="menuOpen" class="menu" role="menu">
            <div class="menu-who">
              <b>{{ me.name }}</b>
              <small>{{ me.email }}</small>
            </div>
            <hr />
            <RouterLink to="/compte-google" role="menuitem" class="menu-link" @click="menuOpen = false"
              >Mon compte Google</RouterLink
            >
            <button type="button" role="menuitem" @click="openNotifications">Notifications</button>
            <button type="button" role="menuitem" @click="emit('logout')">Se déconnecter</button>
          </div>
        </div>
      </div>
      <AppTabs :role="me.role" />
    </div>
  </header>
</template>
